import { useMemo } from 'react';

import { GridColDef } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';

import { ProfilePictureCell } from '../cells/ProfilePictureCell';
import { dataGridCellFontFamilyTabularNumsClassName } from '@/assets/themes/components/MuiDataGrid';
import type { ColumnWidths } from '@/hooks/grid';

interface UseColumnsOptions {
  columnWidths?: ColumnWidths;
}

export const useColumns = ({
  columnWidths,
}: UseColumnsOptions = {}): GridColDef[] => {
  const { t } = useTranslation('employees');

  return useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        field: 'profilePicture',
        headerName: '',
        width: 56,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        disableReorder: true,
        hideable: false,
        display: 'flex',
        renderCell: (params) => (
          <ProfilePictureCell userId={params.row.SourceId} />
        ),
      },
      {
        field: 'Name',
        headerName: t('columns.Name'),
        flex: 1,
        minWidth: 220,
        hideable: false,
      },
      {
        field: 'Position',
        headerName: t('columns.Title'),
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'Email',
        headerName: t('columns.Email'),
        flex: 1,
        minWidth: 220,
        renderCell: (params) => {
          const email = params.value as string | undefined;
          if (!email) return null;
          return (
            <a
              href={`mailto:${email}`}
              onClick={(e) => e.stopPropagation()}
              data-automation-id="employee-email-link"
            >
              {email}
            </a>
          );
        },
      },
      {
        field: 'PrivatePhone',
        headerName: t('columns.Phone'),
        cellClassName: dataGridCellFontFamilyTabularNumsClassName,
        flex: 1,
        minWidth: 140,
      },
      {
        field: 'Mobile',
        headerName: t('columns.Mobile'),
        cellClassName: dataGridCellFontFamilyTabularNumsClassName,
        flex: 1,
        minWidth: 140,
      },
      {
        field: 'DepartmentName',
        headerName: t('columns.Department'),
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'RoleName',
        headerName: t('columns.EmployeeType'),
        flex: 1,
        minWidth: 140,
      },
    ];

    if (columnWidths) {
      return baseColumns.map((col) => {
        const storedWidth = columnWidths[col.field];
        if (
          storedWidth !== undefined &&
          Number.isFinite(storedWidth) &&
          storedWidth >= 0
        ) {
          const { flex: _flex, ...colWithoutFlex } = col;
          return { ...colWithoutFlex, width: storedWidth };
        }
        return col;
      });
    }

    return baseColumns;
  }, [t, columnWidths]);
};
